import React, { useState, useEffect, useContext, useRef } from "react";
import Loader from "../components/Loader";
import { AuthContext } from "../context/AuthContext";
import axiosInstance from "../utils/axiosInstance";

const SupportChatBox = ({ ticketId, role }) => {
  const [newMessage, setNewMessage] = useState("");
  const [ticket, setTicket] = useState();
  const [messages, setMessages] = useState([]);
  const [attachment, setAttachment] = useState(null); // State for attachment
  const [canSend, setCanSend] = useState(false); // Control when the client can send a message
  const [loading, setLoading] = useState(true);
  const [sendMessageLoading, setSendMessageLoading] = useState(false);
  const { user } = useContext(AuthContext);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    const fetchFiles = async () => {
      try {
        const response = await axiosInstance.get(`/tickets/${ticketId}`);
        setTicket(response.data);
        setMessages(response.data.ticket_messages);
        setLoading(false);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchFiles();
  }, [ticketId]);

  useEffect(() => {
    scrollToBottom();
    handleCanSend();
  }, [messages]);

  const handleCanSend = () => {
    // Check if there are any messages
    if (messages.length > 0) {
      // Get the last message
      const lastMessage = messages[messages.length - 1];

      // If the last message was not sent by the current user, enable sending
      if (lastMessage.user_id !== user.id) {
        setCanSend(true);
      } else {
        setCanSend(false);
      }
    } else {
      // If no messages exist, allow sending (e.g., in case the admin hasn't sent a message yet)
      setCanSend(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Handle new message submission
  const sendMessage = async () => {
    if (newMessage.trim() !== "") {
      setSendMessageLoading(true);
      const messageData = {
        ticket_id: ticketId,
        user_id: user.id,
        message: newMessage,
        file_name: attachment ? attachment.name : "",
        created_at: new Date().toLocaleString(), // Generate current date-time
      };

      const formData = new FormData();
      formData.append("jsonData", JSON.stringify(messageData));

      if (attachment) {
        formData.append("file", attachment);
      }

      try {
        const response = await axiosInstance.post(
          `/tickets/${ticketId}/send-message`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        // Update the messages list with the new message
        setMessages([...messages, response.data]);

        // Clear the input fields
        setNewMessage("");
        setAttachment(null);
        setCanSend(false); // Disable sending until the admin responds again
        setSendMessageLoading(false);
      } catch (error) {
        setSendMessageLoading(false);
        console.error("Error sending message:", error);
      }
    }
  };

  // Handle file input for attachment
  const handleAttachment = (e) => {
    setAttachment(e.target.files[0]); // Get the selected file
  };

  // Handle pressing the "Enter" key
  const handleKeyPress = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault(); // Prevent adding a new line
      sendMessage();
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <Loader />
      </div>
    );
  }

  // Determine if the message is from the current user
  const isMessageFromMe = (message) => message.user_id === user.id;

  return (
    <div className="">
      <div className="w-full p-4 rounded-lg bg-white dark:bg-gray-800 dark:border-gray-700">
        <div className="flex justify-between items-center mb-3">
          <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
            Support Chat
          </h4>
        </div>

        <div className="mb-3 h-40 overflow-y-scroll border-b border-gray-300 dark:border-gray-600 pb-3">
          {messages && messages.length > 0 ? (
            messages.map((msg, index) => (
              <div
                key={index}
                className={`flex flex-col mb-2 ${
                  isMessageFromMe(msg) ? "items-end" : "items-start"
                }`}
              >
                <div className="text-sm font-semibold mb-1 text-gray-600 dark:text-gray-300">
                  {isMessageFromMe(msg)
                    ? "Me"
                    : role.toUpperCase() === "ADMIN"
                    ? "Client"
                    : "Support Agent"}{" "}
                  {/* Display 'Me' for current user */}
                </div>
                <div
                  className={`p-2 rounded-md max-w-xs break-words overflow-wrap w-fit ${
                    isMessageFromMe(msg)
                      ? "bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-100"
                      : "bg-blue-600 text-white"
                  }`}
                >
                  {msg.message}
                  {msg.file_name && (
                    <div className="mt-1 text-sm text-blue-500">
                      Attachment: {msg.file_name}
                    </div>
                  )}
                </div>
              </div>
            ))
          ) : (
            <p className="text-gray-500 dark:text-gray-400">
              No messages yet...
            </p>
          )}
          <div ref={messagesEndRef} />{" "}
          {/* Scroll anchor for the last message */}
        </div>

        <div className="mb-3 flex items-center">
          <textarea
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={handleKeyPress}
            rows="1"
            className="flex-grow p-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md focus:outline-none focus:border-blue-500 dark:focus:border-blue-400"
            placeholder="Type your message here..."
            disabled={!canSend} // Disable the input if the client can't send a message
          />
          <label
            htmlFor="fileInput"
            className="ml-2 cursor-pointer flex items-center p-2 rounded-full bg-gray-100 dark:bg-gray-600 hover:bg-gray-200 dark:hover:bg-gray-500"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="size-6"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="m18.375 12.739-7.693 7.693a4.5 4.5 0 0 1-6.364-6.364l10.94-10.94A3 3 0 1 1 19.5 7.372L8.552 18.32m.009-.01-.01.01m5.699-9.941-7.81 7.81a1.5 1.5 0 0 0 2.112 2.13"
              />
            </svg>
          </label>
          <input
            id="fileInput"
            type="file"
            onChange={handleAttachment}
            className="hidden"
            disabled={!canSend} // File input for attachments (hidden)
          />
        </div>

        <button
          onClick={sendMessage}
          className="w-full py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
          disabled={!canSend || sendMessageLoading}
        >
          {sendMessageLoading ? "Sending..." : "Send Message"}
        </button>
      </div>
    </div>
  );
};

export default SupportChatBox;
